import type { ReactNode } from "react";
import type { CompareRow } from "@aura/shared";
import { css } from "styled-system/css";
import { tbl } from "~/components/editorial";
import { fmtInt, fmtNum, fmtPct } from "~/lib/format";
import { signed, signedPct } from "./story";

export interface ExtraColumn<R> {
  head: string;
  cell: (row: R) => ReactNode;
}

/**
 * Previous vs. current 90 days, one row per org, product, repo or author,
 * sorted by the server (current merged, top down). Totals go in the footer.
 */
export function CompareTable<R extends CompareRow>({
  nameHead,
  rows,
  extra = [],
  totals = [],
  periods,
  empty = "No merged pull requests in either period.",
}: {
  nameHead: string;
  /** Short names of the compared periods, e.g. { previous: "Aug", current: "Sep" }. */
  periods: { previous: string; current: string };
  rows: R[];
  extra?: ExtraColumn<R>[];
  totals?: Array<{ row: CompareRow; label: string }>;
  empty?: string;
}) {
  if (rows.length === 0) return <div className={tbl.count}>{empty}</div>;
  const blank = extra.map((c) => <td key={c.head} />);
  return (
    <div className={tbl.scroll}>
      <table className={tbl.numeric}>
        <thead>
          <tr>
            <th>{nameHead}</th>
            {extra.map((c) => (
              <th key={c.head}>{c.head}</th>
            ))}
            <th data-num>{periods.previous}</th>
            <th data-num>{periods.current}</th>
            <th data-num>Change</th>
            <th data-num>Change %</th>
            <th data-num>Checked {periods.previous}</th>
            <th data-num>Checked {periods.current}</th>
            <th data-num>Median h to 1st review</th>
            <th data-num>Median h to merge</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td data-text className={styles.name}>
                {r.label}
              </td>
              {extra.map((c) => (
                <td key={c.head} data-text className={styles.extra}>
                  {c.cell(r)}
                </td>
              ))}
              <Numbers row={r} />
            </tr>
          ))}
        </tbody>
        {totals.length > 0 && (
          <tfoot className={styles.foot}>
            {totals.map((t) => (
              <tr key={t.label}>
                <td data-text className={styles.name}>
                  {t.label}
                </td>
                {blank}
                <Numbers row={t.row} />
              </tr>
            ))}
          </tfoot>
        )}
      </table>
    </div>
  );
}

function Numbers({ row }: { row: CompareRow }) {
  const { previous: p, current: c } = row;
  return (
    <>
      <td data-num data-dim>
        {fmtInt(p.merged)}
      </td>
      <td data-num>{fmtInt(c.merged)}</td>
      <td data-num>{signed(row.change)}</td>
      <td data-num>{signedPct(row) || "–"}</td>
      <td data-num data-dim>
        {fmtPct(p.checkedPct)}
      </td>
      <td data-num>{fmtPct(c.checkedPct)}</td>
      <td data-num>{fmtNum(c.medianHoursToFirstReview)}</td>
      <td data-num>{fmtNum(c.medianHoursToMerge)}</td>
    </>
  );
}

const styles = {
  name: css({ whiteSpace: "nowrap!", fontWeight: 600 }),
  extra: css({ whiteSpace: "nowrap!" }),
  foot: css({
    "& td": {
      fontWeight: 700,
      borderTopWidth: "2px",
      borderTopStyle: "solid",
      borderTopColor: "ed.ink",
    },
    "& tr + tr td": { borderTopWidth: "0" },
  }),
};
